import { useEffect, useState } from 'react'
import { Avatar, IconButton, Tooltip, Typography, useMediaQuery, Box } from '@mui/material'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Close } from '@mui/icons-material'
import { onMessage } from 'firebase/messaging'
import { useSnackbar } from 'notistack';
import useGetNotificationTokenAndSendToBackend from '../../../hooks/useGetNotificationTokenAndSendToBackend'
import { selectAuthUser } from '../../../redux/features/userAuth/userAuthSlice'
import UserMenu from './userMenu'
import { firebaseMessaging, isWebMessagingSupported } from '../../../services/Firebase'
import { isNativePlatform, addNativeMessageListeners } from '../../../services/Firebase/native'
import AppRoutes from '../../../services/AppRoutes'
import AlarmIcon from '../../../images/alarm.svg'
import NotificationsBellsGroup from './NotificationsBellsGroup'

const Index = () => {
  const isXsScreen = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const [anchorElUser, setAnchorElUser] = useState(null)
  const { authorizedUser } = useSelector(selectAuthUser)
  useGetNotificationTokenAndSendToBackend()
  const history = useHistory()

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget)
  }

  const handleCloseUserMenu = () => {
    setAnchorElUser(null)
  }

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const pushSnackbar = ({
    title, description, id,
  }) => enqueueSnackbar('', {
    key: id,
    autoHideDuration: 3000,
    anchorOrigin: {
      vertical: 'top',
      horizontal: !isXsScreen ? 'center' : 'right',
    },
    // Phones only: keeps the card off the screen edges and clear of the app
    // bar, which it was overlapping. env(safe-area-inset-top) covers the notch
    // in the native WebView. Omitted on desktop so notistack's own positioning
    // is untouched.
    ...(!isXsScreen ? {
      style: {
        marginTop: 'calc(env(safe-area-inset-top, 0px) + 8px)',
        marginLeft: '12px',
        marginRight: '12px',
      },
    } : {}),
    content: (
      <Box sx={styles.notificationComponent}>
        <Box sx={styles.notificationCard2}>
          <img src={AlarmIcon} alt="AlarmIcon" />
        </Box>
        {/* minWidth: 0 lets this flex child shrink so the text wraps instead of
            forcing the card wider than the screen. */}
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <Typography sx={styles.time}>
            Just Now
            <Close sx={styles.icon} onClick={() => closeSnackbar(id)} />
          </Typography>

          <Typography sx={styles.title}>
            { title }
          </Typography>

          <Typography sx={styles.description}>
            { description }
          </Typography>

          <Typography
            sx={styles.textBtn}
            onClick={() => history.push(AppRoutes.notificationCenter)}
          >
            Notification Center
          </Typography>
        </Box>

      </Box>
    ),
  });

  useEffect(() => {
    // Native (Capacitor) foreground + tap handling.
    if (isNativePlatform()) {
      let dispose = () => {}
      addNativeMessageListeners({
        onForeground: (notification) => pushSnackbar({
          title: notification?.title,
          description: notification?.body || '',
          id: notification?.id || `${Date.now()}`,
        }),
        onTap: () => history.push(AppRoutes.notificationCenter),
      }).then((remove) => { dispose = remove })
      return () => dispose()
    }

    // Web (browser/PWA) foreground handling via Firebase Web Messaging.
    // Guard against the empty {} fallback returned in unsupported WebViews.
    if (!isWebMessagingSupported) return undefined
    const unsubscribe = onMessage(firebaseMessaging, (msg) => {
      pushSnackbar({
        title: msg?.notification?.title,
        description: msg?.notification?.body || '',
        id: msg?.messageId,
      })
    })
    return () => unsubscribe()
  }, []);

  return (
    <Box sx={styles.root}>

      <NotificationsBellsGroup />

      {isXsScreen && (
        <>
          <Tooltip title='Open settings'>
            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
              <Avatar
                alt={authorizedUser?.name?.toUpperCase() || 'SW'}
                src='/static/images/avatar/2.jpg'
              />
            </IconButton>
          </Tooltip>

          <UserMenu menuState={anchorElUser} changeMenuState={handleCloseUserMenu} />
        </>
      )}
    </Box>
  )
}
const styles = {
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    p: 0,
    gap: '20px',
    '& svg:nth-child(1)': {
      fontSize: '30px',
      cursor: 'pointer',
    },

    '& svg': {
      color: 'white',
    },
  },
  flexBetween: { display: 'flex', justifyContent: 'space-between', my: '5px' },
  sideBtn: {
    border: '0',
    color: 'text.hint',
    padding: '0',
    backgroundColor: 'transparent',
    '&: hover': {
      backgroundColor: 'transparent',
    },
  },
  sideButtonContainer: { width: '290px !important' },
  text: {
    fontSize: '14px',
    fontWeight: '500',
  },
  textColor: { color: '#29CC8B', cursor: 'pointer' },
  cardTime: {
    fontSize: '12px',
    color: '#92929D',
    display: 'block',
  },
  notificationsNoneOutlinedIcon: {
    fill: '#29CC8B',
    fontSize: '25px !important',
    margin: '3px',
  },
  notificationCardList: {
    maxHeight: '300px',
    overflowY: 'scroll',
    '::-webkit-scrollbar': {
      width: '0.3em',
    },
    '::-webkit-scrollbar-track': {
      '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)',
    },
    '::-webkit-scrollbar-thumb': {
      backgroundColor: 'primary.main',
    },
  },
  notificationCard: {
    display: 'flex',
    gap: '16px',
    padding: '10px 0',
  },
  notificationIconContainer: {
    height: '32px',
    width: '32px',
    borderRadius: '10px',
    backgroundColor: '#EAFAF3',
  },
  padge: {
    '& span.MuiBadge-badge.MuiBadge-dot.MuiBadge-anchorOriginTopRight.MuiBadge-anchorOriginTopRightRectangular.MuiBadge-overlapRectangular.MuiBadge-colorError ': {
      transform: 'scale(1) translate(-50%, 30%)',
    },
  },

  notificationComponent: {
    // Fluid on phones only. The old fixed 300px plus the snackbar's own margins
    // pushed the card past the viewport, so it was clipped on the right. Desktop
    // keeps its previous 500px exactly, so nothing shifts there.
    width: { xs: '100%', sm: '500px' },
    maxWidth: '100%',
    // maxHeight was 160px with no overflow handling, which cut the description
    // mid-sentence AND hid the "Notification Center" button underneath it. The
    // description is clamped by line count instead, so the button always shows.
    display: 'flex',
    overflow: 'hidden',
    backgroundColor: '#FFF',
    zIndex: 9999,
    filter: 'drop-shadow(0px 8px 16px rgba(0, 0, 0, 0.16))',
    borderRadius: '12px',
    '&: hover': {
      '& svg': {
        visibility: 'visible',
      },
    },
  },
  notificationCard2: {
    width: { xs: '55px', sm: '80px' },
    flexShrink: 0,
    backgroundColor: '#29CC8B',
    borderRadius: '12px 0px 0px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& img': {
      width: { xs: '40px', sm: '50px' },
      height: { xs: '40px', sm: '50px' },
    },
  },
  time: {
    padding: '10px',
    pb: '0',
    br: '0',
    color: '#AFAFAF',
    fontSize: '12px',
    fontWeight: 300,
    marginRight: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'end',
    columnGap: '11px',
  },
  icon: {
    color: '#29CC8B',
    width: '15px',
    cursor: 'pointer',
    // Reveal-on-hover is kept for pointer devices exactly as before. Touch
    // screens have no hover, so there the close button was permanently
    // invisible and could never be tapped - shown unconditionally there only.
    visibility: 'hidden',
    '@media (hover: none)': {
      visibility: 'visible',
    },
  },
  title: {
    color: '#000',
    fontSize: '16px',
    fontWeight: 500,
    ml: '14px',
  },
  description: {
    color: '#000',
    fontSize: '14px',
    fontWeight: 300,
    fontStyle: 'normal',
    ml: '14px',
    mt: '5px',
    marginInlineEnd: '14px',
    // Was a fixed 230px/385px, which overflowed the card on narrow screens.
    // Clamped to three lines so the card height stays predictable whatever the
    // notification body contains.
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  textBtn: {
    color: '#29CC8B',
    fontSize: '13px',
    fontWeight: 500,
    fontStyle: 'normal',
    width: 'fit-content',
    textAlign: 'right',
    cursor: 'pointer',
    p: '5px 15px',
    borderRadius: '12px',
    display: 'flex',
    marginLeft: 'auto',
    marginBottom: '4px',
    transition: 'all .5s',
    mr: '4px',
    alignSelf: 'end',
    '&: hover': {
      backgroundColor: '#ECEFF1',
    },
  },

}

export default Index
